import type { MeetingDetail } from '@fos/shared/contracts/meetings';
import { CALENDAR_MEETING_TEXTS } from '@fos/shared/labels';
import { Archive, ArchiveRestore, Link2, Lock, Pencil, Plus, RefreshCw, Users } from 'lucide-react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { MenuItem, MenuSeparator } from '../../../ui/Menu';
import { toast } from '../../../ui/Toast';
import { meetingHref } from '../meeting-format';

/**
 * Akcje na rekordzie spotkania wspólne dla widoku spotkania i wiersza listy (10.10.2026: menu „⋯” w każdym wierszu
 * /spotkania i zakładki „Spotkania” klienta i projektu): archiwizacja z „Cofnij”, przywrócenie, udostępnienie
 * spotkania z kalendarza osobistego zespołowi i cofnięcie. Wynik obsługują opcje mutacji (nie wywołania), więc
 * odświeżenie list i komunikat działają także wtedy, gdy wiersz po archiwizacji zniknął z listy. Uprawnienia
 * sprawdza backend; UI tylko nie pokazuje akcji, których osoba i tak by nie wykonała.
 */
export function useMeetingRecordActions() {
  const utils = trpc.useUtils();
  const restore = trpc.meetings.restore.useMutation({
    onSuccess: async () => {
      await utils.meetings.invalidate();
      toast.success('Przywrócono spotkanie');
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się przywrócić spotkania.')),
  });
  const archive = trpc.meetings.archive.useMutation({
    onSuccess: async (saved) => {
      await utils.meetings.invalidate();
      toast.success('Spotkanie trafiło do archiwum', {
        action: { label: 'Cofnij', onClick: () => restore.mutate({ id: saved.id, version: saved.version }) },
      });
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zarchiwizować spotkania.')),
  });
  const share = trpc.calendar.shareMeeting.useMutation({
    onSuccess: async (saved) => {
      await utils.meetings.invalidate();
      toast.success(saved.calendar?.shared ? CALENDAR_MEETING_TEXTS.sharedToast : CALENDAR_MEETING_TEXTS.unsharedToast);
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zmienić udostępnienia.')),
  });
  return {
    archive: (m: Pick<MeetingDetail, 'id' | 'version'>) => archive.mutate({ id: m.id, version: m.version }),
    restore: (m: Pick<MeetingDetail, 'id' | 'version'>) => restore.mutate({ id: m.id, version: m.version }),
    /** udostępnij zespołowi / cofnij udostępnienie (tylko właściciel kalendarza — `calendar.canShare`) */
    toggleShare: (m: Pick<MeetingDetail, 'id' | 'version' | 'calendar'>) =>
      share.mutate({ id: m.id, version: m.version, shared: !m.calendar?.shared }),
    sharing: share.isPending,
  };
}

export type MeetingRecordActions = ReturnType<typeof useMeetingRecordActions>;

export function copyMeetingLink(id: string) {
  void navigator.clipboard
    ?.writeText(`${window.location.origin}${meetingHref(id)}`)
    .then(() => toast.success('Skopiowano link do spotkania'))
    .catch(() => toast.error('Nie udało się skopiować linku'));
}

/**
 * Pozycje menu „⋯” spotkania (te same w widoku spotkania i w wierszu listy). Dialogi (edycja, transkrypcja, nowe
 * zadanie) i mutacje trzyma rodzic — menu po wyborze pozycji się zamyka.
 */
export function MeetingMenuItems({
  meeting,
  actions,
  onEdit,
  onEditTranscript,
  onAddTask,
  onProcess,
  processing,
}: {
  meeting: MeetingDetail;
  actions: MeetingRecordActions;
  onEdit: () => void;
  onEditTranscript: () => void;
  onAddTask: () => void;
  onProcess: () => void;
  processing: boolean;
}) {
  const access = useAccess();
  const canWriteClients = access.can('clients', 'edit');
  const canAddTask = meetingCanAddTask(meeting, access.can('tasks', 'edit'));
  const reprocessable =
    meeting.canProcess && (meeting.ai.status === 'done' || meeting.ai.status === 'failed' || meeting.ai.stuck);
  const calendar = meeting.calendar;
  const canShare = Boolean(calendar?.canShare) && !meeting.archivedAt;

  return (
    <>
      {meeting.canEdit && (
        <MenuItem icon={Pencil} onSelect={onEdit}>
          Edytuj spotkanie
        </MenuItem>
      )}
      {meeting.canEdit && (
        <MenuItem icon={Pencil} onSelect={onEditTranscript}>
          {meeting.hasTranscript ? 'Edytuj transkrypcję' : 'Dodaj transkrypcję'}
        </MenuItem>
      )}
      {reprocessable && (
        <MenuItem icon={RefreshCw} disabled={processing} onSelect={onProcess}>
          Przetwórz ponownie
        </MenuItem>
      )}
      {canAddTask && (
        <MenuItem icon={Plus} onSelect={onAddTask}>
          Dodaj zadanie ręcznie
        </MenuItem>
      )}
      {canShare && (
        <MenuItem
          icon={calendar?.shared ? Lock : Users}
          disabled={actions.sharing}
          onSelect={() => actions.toggleShare(meeting)}
        >
          {calendar?.shared ? CALENDAR_MEETING_TEXTS.unshare : CALENDAR_MEETING_TEXTS.share}
        </MenuItem>
      )}
      <MenuItem icon={Link2} onSelect={() => copyMeetingLink(meeting.id)}>
        Kopiuj link
      </MenuItem>
      {canWriteClients && (meeting.canEdit || meeting.archivedAt) && (
        <>
          <MenuSeparator />
          {meeting.archivedAt ? (
            <MenuItem icon={ArchiveRestore} onSelect={() => actions.restore(meeting)}>
              Przywróć z archiwum
            </MenuItem>
          ) : (
            <MenuItem icon={Archive} onSelect={() => actions.archive(meeting)}>
              Archiwizuj
            </MenuItem>
          )}
        </>
      )}
    </>
  );
}

/** „Dodaj zadanie ręcznie”: Zadania = Edycja i spotkanie poza archiwum. */
export function meetingCanAddTask(meeting: Pick<MeetingDetail, 'archivedAt'>, canEditTasks: boolean): boolean {
  return canEditTasks && !meeting.archivedAt;
}
